import React, { useState, useEffect, useRef, Component, memo } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  AccordionTitle,
  AccordionContent,
  Accordion,
  Container,
  Dimmer,
  Grid,
  GridRow,
  GridColumn,
  Form,
  Select,
  Input,
  FormInput,
  TabPane,
  Tab,
  Button,
  TextArea,
  Checkbox,
  Icon,
  Segment,
  Label,
  Search,
  ButtonGroup,
  Menu,
  Loader,
} from "semantic-ui-react";
import "../scss/common.scss";
import "../scss/common.scss";
import { ReactComponent as IconImage } from "../assets/images/icon-img.svg";
import "lightbox2/dist/css/lightbox.css";
import "lightbox2/dist/js/lightbox-plus-jquery.js";

function TabComponent({
  logs,
  setLoadingStatus,
  fetchTradeLogs,
  categoryOptions,
  paymentOptions,
  counterparty,
  setCounterparty,
  fetchFilterSort,
  fetchCompanies,
}) {
  const { userId } = useAuth(); // ユーザーID
  const [date, setDate] = useState(""); // 日付
  const [amount, setAmount] = useState(""); // 金額
  const [category, setCategory] = useState(""); // カテゴリー
  const [paymentMethod, setPaymentMethod] = useState(""); // 決済手段
  const [csv, setCsv] = useState(""); // CSV
  // 画像アップロード用
  const [images, setImages] = useState([]);
  const [inputKey, setInputKey] = useState(Date.now());
  const inputRef = useRef(null);

  // 登録(form)
  const handleFormSubmit = async () => {
    setLoadingStatus(true);
    // 日付チェック
    if (!isValidDate(date)) {
      alert("日付を正しく入力してください");
      setLoadingStatus(false);
      return; // 送信を中止
    }
    const tradeData = {
      date: date,
      counterparty: counterparty,
      amount: amount ? parseInt(amount, 10) : 0,
      category: category,
      payment_method: paymentMethod,
      user_id: userId,
    };

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/insert_trade/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(tradeData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // 送信が成功後、useStateを空
      setDate("");
      setCounterparty("");
      setAmount("");
      setCategory("");
      setPaymentMethod("");

      // 最新の取引情報を取得
      fetchFilterSort();
      fetchTradeLogs();
      fetchCompanies();

      const result = await response.json();
      setLoadingStatus(false);
      alert("登録に成功しました");
    } catch (error) {
      setLoadingStatus(false);
      alert("登録に失敗しました");
      console.error("There was a problem with the fetch operation:", error);
    }
  };

  // 登録(CSV)
  const handleCsvSubmit = async () => {
    setLoadingStatus(true);
    // カンマ区切り文字列を配列に変換
    const csvAry = parseCsvToObjects(csv);
    // 配列の生成に失敗した場合
    if (csvAry.error) {
      setLoadingStatus(false);
      alert(csvAry.message);
      return;
    }

    // 配列のバリデーション
    const validFlg = validationCsvAry(csvAry.data);
    if (!validFlg) {
      setLoadingStatus(false);
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/insert_trades_csv/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(csvAry.data),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // 送信が成功後、useStateを空
      setCsv("");

      // 最新の取引情報を取得
      fetchFilterSort();
      fetchTradeLogs();
      fetchCompanies();

      const result = await response.json();
      setLoadingStatus(false);
      alert(`${csvAry.data.length}件の登録に成功しました`);
    } catch (error) {
      setLoadingStatus(false);
      alert("登録に失敗しました");
      console.error("There was a problem with the fetch operation:", error);
    }
  };

  // CSVを配列に変換
  function parseCsvToObjects(csvText) {
    // 日付, 取引先, 金額, カテゴリ, 決済手段
    const expectedFieldCount = 5;
    const lines = csvText.split("\n");
    // 空行をフィルタリング
    const filteredLines = lines.filter((line) => line.trim() !== "");
    const csvAry = [];

    // 行ごとにオブジェクトに形成し、配列に変換
    for (let i = 0; i < filteredLines.length; i++) {
      const line = filteredLines[i];
      const parts = line.split(",");

      // フィールド数のチェック
      if (parts.length !== expectedFieldCount) {
        return {
          error: true,
          message: `${
            i + 1
          }行目: フィールドの数が期待される数に満たないため、処理を停止しました。`,
        };
      }

      // キーに割り当て
      csvAry.push({
        date: parts[0].trim(),
        counterparty: parts[1].trim(),
        amount: parts[2].trim() ? parts[2].trim() : 0,
        category: parts[3].trim(),
        payment_method: parts[4].trim(),
        user_id: userId,
      });
    }

    return { error: false, data: csvAry };
  }

  // バリデーション(CSV)
  function validationCsvAry(csvAry) {
    // 有効なカテゴリのリスト
    const validCategories = categoryOptions.map((option) => option.text);
    // 有効な決済手段のリスト
    const validPaymentMethods = paymentOptions.map((option) => option.text);

    let validationMessages = [];

    csvAry.forEach((item, index) => {
      // 日付チェック
      if (!isValidDate(item.date)) {
        validationMessages.push(`${index + 1}行目: 無効な日付 - ${item.date}`);
      }
      // カテゴリーチェック
      if (item.category && !validCategories.includes(item.category)) {
        validationMessages.push(
          `${index + 1}行目: 無効なカテゴリ - ${item.category}`
        );
      }
      // 決済手段チェック
      if (
        item.payment_method &&
        !validPaymentMethods.includes(item.payment_method)
      ) {
        validationMessages.push(
          `${index + 1}行目: 無効な決済手段 - ${item.payment_method}`
        );
      }
    });

    // エラーがある場合
    if (validationMessages.length > 0) {
      alert(validationMessages.join("\n"));
      return false;
    }

    return true;
  }

  // stateに画像をセット
  const handleImageChange = (event) => {
    setImages([...event.target.files]);
  };

  // 画像をAPIに送信
  const handleImageSubmit = async () => {
    setLoadingStatus(true);
    const formData = new FormData();
    images.forEach((image) => {
      formData.append("images", image);
    });

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/image_upload/`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // inputタグの内容をクリア
      if (inputRef.current) {
        inputRef.current.value = null;
      }
      // inputのキーを更新してリセット
      setInputKey(Date.now());
      setImages([]);
      // 最新の取引情報を取得
      fetchFilterSort();
      fetchTradeLogs();
      fetchCompanies();
      setLoadingStatus(false);
      alert("アップロードが完了しました");
    } catch (error) {
      setLoadingStatus(false);
      alert("登録に失敗しました");
      console.error("There was a problem with the fetch operation:", error);
    }
  };

  // 日付のフォーマットを確認
  function isValidDate(dateString) {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateString.match(regex)) {
      // フォーマットが YYYY-MM-DD に一致しない場合は無効
      return false;
    }

    const date = new Date(dateString);
    const timestamp = date.getTime();

    if (typeof timestamp !== "number" || Number.isNaN(timestamp)) {
      // 日付が有効でない場合は無効
      return false;
    }

    return date.toISOString().startsWith(dateString);
  }

  // タブ
  const panes = [
    {
      menuItem: "form",
      render: () => (
        <TabPane /*loading*/>
          <Grid celled>
            <GridRow celled>
              {/* 日付 */}
              <GridColumn width={2}>
                <Form size="mini">
                  <Input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </Form>
              </GridColumn>
              {/* 取引先 */}
              <GridColumn width={5}>
                <Form size="mini">
                  <FormInput
                    fluid
                    placeholder="取引先"
                    value={counterparty}
                    onChange={(e) => setCounterparty(e.target.value)}
                  />
                </Form>
              </GridColumn>
            </GridRow>

            <GridRow celled>
              {/* 金額 */}
              <GridColumn width={2}>
                <Form size="mini">
                  <FormInput
                    type="number"
                    fluid
                    placeholder="金額"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </Form>
              </GridColumn>
              {/* カテゴリ */}
              <GridColumn width={2}>
                <Form size="mini">
                  <Select
                    key={category}
                    placeholder="category"
                    options={categoryOptions}
                    style={{ width: "100%", minWidth: "initial" }}
                    value={category}
                    onChange={(e, { value }) => setCategory(value)}
                  />
                </Form>
              </GridColumn>
              {/* 決済手段 */}
              <GridColumn width={2}>
                <Form size="mini">
                  <Select
                    key={paymentMethod}
                    placeholder="決済手段"
                    options={paymentOptions}
                    style={{
                      width: "100%",
                      minWidth: "initial",
                    }}
                    value={paymentMethod}
                    onChange={(e, { value }) => setPaymentMethod(value)}
                  />
                </Form>
              </GridColumn>
            </GridRow>
          </Grid>
          <div className="">
            <Button primary onClick={handleFormSubmit}>
              登録
            </Button>
          </div>
        </TabPane>
      ),
    },
    {
      menuItem: "CSV",
      render: () => (
        <TabPane /*loading*/>
          <Grid celled>
            <GridRow celled>
              <GridColumn width={16}>
                <Form>
                  <TextArea
                    placeholder="日付,取引先,金額,カテゴリ,決済手段(カンマ区切りでまとめて追加)"
                    value={csv}
                    onChange={(e) => setCsv(e.target.value)}
                  />
                </Form>
              </GridColumn>
            </GridRow>
          </Grid>

          <div className="">
            <Button primary onClick={handleCsvSubmit}>
              登録
            </Button>
          </div>
        </TabPane>
      ),
    },
    {
      menuItem: "log",
      render: () => (
        <TabPane className="log">
          {logs.map((log, index) => (
            <div key={index}>
              <ul className={log.status}>
                <li>{log.created_at}</li>
                <li>{log.date}</li>
                <li>{log.counterparty}</li>
                <li>{log.amount}</li>
                <li>{log.category}</li>
                <li>{log.payment_method}</li>
                <li>{log.settlement_date}</li>
                <li>{log.username}</li>
              </ul>
            </div>
          ))}
        </TabPane>
      ),
    },
    {
      menuItem: "upload",
      render: () => (
        <TabPane className="upload">
          <Input
            type="file"
            multiple
            onChange={handleImageChange}
            ref={inputRef}
            key={inputKey}
          />
          <Button primary onClick={handleImageSubmit}>
            Upload
          </Button>
        </TabPane>
      ),
    },
  ];

  return <Tab panes={panes} />;
}

export default memo(TabComponent);
